import React, { useState } from 'react';
import { XCircle, AlertTriangle, Info, Clock, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function ErrorList({ errors, onResolve }) {
  const [expandedError, setExpandedError] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveModal, setShowResolveModal] = useState(null);

  const getSeverityIcon = (severity) => {
    const iconMap = {
      critical: XCircle,
      warning: AlertTriangle,
      info: Info
    };
    return iconMap?.[severity] || Info;
  };

  const getSeverityColor = (severity) => {
    const colorMap = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      warning: 'bg-orange-100 text-orange-800 border-orange-300',
      info: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colorMap?.[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      unresolved: 'bg-red-100 text-red-800',
      investigating: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      ignored: 'bg-gray-100 text-gray-800'
    };
    return colorMap?.[status] || 'bg-gray-100 text-gray-800';
  };

  const handleResolve = async (errorId) => {
    await onResolve(errorId, resolutionNotes);
    setShowResolveModal(null);
    setResolutionNotes('');
  };

  if (!errors || errors?.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-gray-500">No errors found for the selected filters</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {errors?.map((error) => {
        const SeverityIcon = getSeverityIcon(error?.severity);
        return (
          <div
            key={error?.id}
            className={`border-2 rounded-lg p-4 ${getSeverityColor(error?.severity)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <SeverityIcon className="w-5 h-5" />
                  <span className="px-3 py-1 bg-white rounded-full text-xs font-bold uppercase">
                    {error?.severity}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(error?.resolutionStatus)}`}>
                    {error?.resolutionStatus?.replace('_', ' ')}
                  </span>
                  <span className="text-xs opacity-75">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {format(new Date(error?.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2">{error?.errorType}</h3>
                <p className="text-sm mb-3">{error?.errorMessage}</p>
                {error?.errorCode && (
                  <p className="text-xs font-mono mb-2">Code: {error?.errorCode}</p>
                )}
                <div className="flex items-center space-x-4 text-xs">
                  {error?.affectedUser && (
                    <span>
                      User: {error?.affectedUser?.first_name} {error?.affectedUser?.last_name}
                    </span>
                  )}
                  {error?.affectedFeature && (
                    <span>Feature: {error?.affectedFeature}</span>
                  )}
                  {error?.ipAddress && (
                    <span>IP: {error?.ipAddress}</span>
                  )}
                </div>
              </div>
              <div className="ml-4 flex flex-col space-y-2">
                {error?.resolutionStatus === 'unresolved' && (
                  <button
                    onClick={() => setShowResolveModal(error?.id)}
                    className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                  >
                    Resolve
                  </button>
                )}
                <button
                  onClick={() => setExpandedError(expandedError === error?.id ? null : error?.id)}
                  className="p-2 hover:bg-white hover:bg-opacity-50 rounded-lg"
                >
                  {expandedError === error?.id ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {expandedError === error?.id && (
              <div className="mt-4 pt-4 border-t border-current opacity-75">
                {error?.stackTrace && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold mb-2">Stack Trace</h4>
                    <pre className="bg-white bg-opacity-50 p-3 rounded text-xs overflow-auto max-h-40">
                      {error?.stackTrace}
                    </pre>
                  </div>
                )}
                {error?.contextData && Object.keys(error?.contextData)?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold mb-2">Context Data</h4>
                    <pre className="bg-white bg-opacity-50 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(error?.contextData, null, 2)}
                    </pre>
                  </div>
                )}
                {error?.resolutionStatus === 'resolved' && error?.resolvedByUser && (
                  <div>
                    <h4 className="text-xs font-semibold mb-2">Resolution Details</h4>
                    <p className="text-xs">
                      Resolved by: {error?.resolvedByUser?.first_name} {error?.resolvedByUser?.last_name}
                    </p>
                    <p className="text-xs">
                      Resolved at: {format(new Date(error?.resolvedAt), 'MMM dd, yyyy HH:mm:ss')}
                    </p>
                    {error?.resolutionNotes && (
                      <p className="text-xs mt-2">
                        Notes: {error?.resolutionNotes}
                      </p>
                    )}
                  </div>
                )}
                {error?.userAgent && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold mb-1">User Agent</h4>
                    <p className="text-xs">{error?.userAgent}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Resolve Error</h3>
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e?.target?.value)}
              placeholder="Enter resolution notes..."
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 h-32"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowResolveModal(null);
                  setResolutionNotes('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleResolve(showResolveModal)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}